import React, { useEffect, useState } from "react";
import Calendar from "../../components/calendar/Calendar";
import convertDateFormat from "../../hooks/convertDate";
import {
  fetchGuarantors,
  fetchGuarantorsSummary,
} from "../../sdk/guarantors/guarantors";
import { Helmet } from "react-helmet";
import Requestors from "../../pages/loans/Requestors";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import ExportGuarantorExposureCSV from "../../components/export/ExportGuarantorExposureCSV";
import { GuarantorRowSkeleton } from "../../skeletons/MySkeleton";

const Guarantors = () => {
  const [guarantors, setGuarantors] = useState([]);
  const [summary, setSummary] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customerFocusedInput, setCustomerFocusedInput] = useState(null);

  const [query, setQuery] = useState("");

  let [totalPages, setTotalPages] = useState(null);
  const [displayedPage, setDisplayedPage] = useState(1);
  let [currentPage, setCurrentPage] = useState(1);

  const [limit, setLimit] = useState(10);
  const [totalGuarantors, settotalGuarantors] = useState([]);
  const [loadingGuarantors, setLoadingGuarantors] = useState(false);

  const getguarantorsData = async () => {
    setLoadingGuarantors(true);
    try {
      const response = await fetchGuarantors(
        currentPage,
        limit,
        query,
        convertDateFormat(fromDate),
        convertDateFormat(toDate),
      );

      setGuarantors(response?.data || []);
      setDisplayedPage(response?.meta?.page || 1);
      setTotalPages(response?.meta?.totalPages || 1);
      settotalGuarantors(response?.meta?.total || 0);
    } catch (error) {
      console.error("Failed to fetch guarantors data:", error);
      setGuarantors([]);
    } finally {
      setLoadingGuarantors(false);
    }
  };

  const getGuarantorsSummary = async () => {
    try {
      const data = await fetchGuarantorsSummary();
      setSummary(data);
    } catch (err) {
      console.error("Fetch summary error:", err);
      setSummary(null);
    }
  };

  useEffect(() => {
    getGuarantorsSummary();
  }, []);

  useEffect(() => {
    getguarantorsData();
  }, [currentPage, limit, query, fromDate, toDate]);

  const [refreshLoading, setRefreshLoading] = useState(false);

  const handleRefetch = async () => {
    setRefreshLoading(true);
    await getguarantorsData();
    setRefreshLoading(false);
  };

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === i
              ? "bg-[#005C53] text-white"
              : "bg-[#F5F5F5] text-[#005C53]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }

    return pages;
  };

  const [selectedGuarantorId, setSelectedGuarantorId] = useState(null);
  const [selectedGuarantor, setSelectedGuarantor] = useState(null);
  const [isRequestorsOpen, SetIsRequestorsOpen] = useState(false);

  const openRequestorsOpen = () => {
    SetIsRequestorsOpen(true);
  };

  const closeRequestorsOpen = () => {
    SetIsRequestorsOpen(false);
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Sojrel || Guarantors</title>
      </Helmet>
      <div className="w-[100%] h-full overflow-y-auto">
        <div className="my-[5px]">
          <p className="text-[#005C53] text-[20px] w-[97%] mx-auto font-bold">
            Guarantors
          </p>
        </div>
        <PermissionGate required={[PERMISSIONS?.LN?.VIEW_GUARANTORS_SUMMARY]}>
          <div className="flex w-[97%] gap-3  mx-auto mt-10">
            <div className="flex-col flex items-center justify-center w-[28.7%] bg-[#E0FFEB] rounded-[10px] border-[1px] border-[#005C53]">
              <div className="flex items-center justify-center mt-3 w-[32px] h-[32px] rounded-[30px] bg-[#BFE0FD]">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="16" cy="16" r="16" fill="#8ED6A8" />
                  <path
                    d="M20.9628 14.5462C21.5 14.009 21.5 13.1455 21.5 11.4167C21.5 9.68783 21.5 8.82433 20.9628 8.28717M20.9628 14.5462C20.4257 15.0833 19.5622 15.0833 17.8333 15.0833H14.1667C12.4378 15.0833 11.5743 15.0833 11.0372 14.5462M20.9628 8.28717C20.4257 7.75 19.5622 7.75 17.8333 7.75H14.1667C12.4378 7.75 11.5743 7.75 11.0372 8.28717M11.0372 8.28717C10.5 8.82433 10.5 9.68783 10.5 11.4167C10.5 13.1455 10.5 14.009 11.0372 14.5462M16.9167 11.4167C16.9167 11.6598 16.8201 11.8929 16.6482 12.0648C16.4763 12.2368 16.2431 12.3333 16 12.3333C15.7569 12.3333 15.5237 12.2368 15.3518 12.0648C15.1799 11.8929 15.0833 11.6598 15.0833 11.4167C15.0833 11.1736 15.1799 10.9404 15.3518 10.7685C15.5237 10.5966 15.7569 10.5 16 10.5C16.2431 10.5 16.4763 10.5966 16.6482 10.7685C16.8201 10.9404 16.9167 11.1736 16.9167 11.4167Z"
                    stroke="#0C722F"
                  />
                  <path
                    d="M21.4997 10.5C20.7703 10.5 20.0709 10.2103 19.5551 9.69454C19.0394 9.17882 18.7497 8.47935 18.7497 7.75M21.4997 12.3333C20.7703 12.3333 20.0709 12.6231 19.5551 13.1388C19.0394 13.6545 18.7497 14.354 18.7497 15.0833M10.4997 10.5C11.229 10.5 11.9285 10.2103 12.4442 9.69454C12.9599 9.17882 13.2497 8.47935 13.2497 7.75M10.4997 12.3333C11.229 12.3333 11.9285 12.6231 12.4442 13.1388C12.9599 13.6545 13.2497 14.354 13.2497 15.0833M9.58301 23.689H11.6547C12.5805 23.689 13.5183 23.7862 14.4193 23.9713C16.0281 24.3018 17.6834 24.3385 19.3052 24.0795C20.1008 23.9512 20.8818 23.7541 21.5895 23.4131C22.2275 23.1042 23.0094 22.6706 23.5347 22.1838C24.059 21.698 24.6053 20.9032 24.9922 20.2817C25.3258 19.7482 25.1645 19.0947 24.6383 18.6977C24.3427 18.4827 23.9865 18.3668 23.6208 18.3668C23.2552 18.3668 22.899 18.4827 22.6033 18.6977L20.9469 19.949C20.3053 20.4348 19.604 20.8812 18.7689 21.0142C18.6681 21.0297 18.5627 21.0444 18.4527 21.0572M18.4527 21.0572C18.4191 21.0611 18.3855 21.0648 18.3518 21.0682M18.4527 21.0572C18.5994 21.0176 18.7339 20.942 18.8441 20.8372C18.9822 20.7178 19.095 20.5718 19.1758 20.408C19.2566 20.2442 19.3038 20.0659 19.3146 19.8835C19.3253 19.7012 19.2995 19.5186 19.2385 19.3464C19.1775 19.1742 19.0827 19.016 18.9596 18.8811C18.8401 18.7485 18.701 18.635 18.5471 18.5447C15.9832 17.0147 11.9929 18.1798 9.58301 19.8894M18.4527 21.0572C18.4195 21.0645 18.3857 21.0682 18.3518 21.0682M18.3518 21.0682C17.799 21.1238 17.2421 21.125 16.689 21.0719"
                    stroke="#0C722F"
                    stroke-linecap="round"
                  />
                  <path
                    d="M9.58301 19.2085C9.58301 18.4491 8.9674 17.8335 8.20801 17.8335C7.44862 17.8335 6.83301 18.4491 6.83301 19.2085V23.7918C6.83301 24.5512 7.44862 25.1668 8.20801 25.1668C8.9674 25.1668 9.58301 24.5512 9.58301 23.7918V19.2085Z"
                    stroke="#0C722F"
                  />
                </svg>
              </div>
              <div className="flex justify-center mt-3 gap-2">
                <p className="text-[#005C53] text-[24px] font-bold">
                  KES{" "}
                  {Number(summary?.totalGuaranteed || "0.00")?.toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  )}
                </p>
              </div>
              <p className="mt-3 mb-3 text-[#005C53] text-[12px]">
                Total Amount Guaranteed
              </p>
            </div>
            <div className="flex-col flex items-center justify-center w-[28.7%] rounded-[10px] border-[1px] border-[#E6E6E6]">
              <div className="flex items-center justify-center mt-3 w-[32px] h-[32px] rounded-[30px] bg-[#BFFDE3]">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="16" cy="16" r="16" fill="#8ED6A8" />
                  <path
                    d="M20.9628 14.5462C21.5 14.009 21.5 13.1455 21.5 11.4167C21.5 9.68783 21.5 8.82433 20.9628 8.28717M20.9628 14.5462C20.4257 15.0833 19.5622 15.0833 17.8333 15.0833H14.1667C12.4378 15.0833 11.5743 15.0833 11.0372 14.5462M20.9628 8.28717C20.4257 7.75 19.5622 7.75 17.8333 7.75H14.1667C12.4378 7.75 11.5743 7.75 11.0372 8.28717M11.0372 8.28717C10.5 8.82433 10.5 9.68783 10.5 11.4167C10.5 13.1455 10.5 14.009 11.0372 14.5462M16.9167 11.4167C16.9167 11.6598 16.8201 11.8929 16.6482 12.0648C16.4763 12.2368 16.2431 12.3333 16 12.3333C15.7569 12.3333 15.5237 12.2368 15.3518 12.0648C15.1799 11.8929 15.0833 11.6598 15.0833 11.4167C15.0833 11.1736 15.1799 10.9404 15.3518 10.7685C15.5237 10.5966 15.7569 10.5 16 10.5C16.2431 10.5 16.4763 10.5966 16.6482 10.7685C16.8201 10.9404 16.9167 11.1736 16.9167 11.4167Z"
                    stroke="#0C722F"
                  />
                  <path
                    d="M21.4997 10.5C20.7703 10.5 20.0709 10.2103 19.5551 9.69454C19.0394 9.17882 18.7497 8.47935 18.7497 7.75M21.4997 12.3333C20.7703 12.3333 20.0709 12.6231 19.5551 13.1388C19.0394 13.6545 18.7497 14.354 18.7497 15.0833M10.4997 10.5C11.229 10.5 11.9285 10.2103 12.4442 9.69454C12.9599 9.17882 13.2497 8.47935 13.2497 7.75M10.4997 12.3333C11.229 12.3333 11.9285 12.6231 12.4442 13.1388C12.9599 13.6545 13.2497 14.354 13.2497 15.0833M9.58301 23.689H11.6547C12.5805 23.689 13.5183 23.7862 14.4193 23.9713C16.0281 24.3018 17.6834 24.3385 19.3052 24.0795C20.1008 23.9512 20.8818 23.7541 21.5895 23.4131C22.2275 23.1042 23.0094 22.6706 23.5347 22.1838C24.059 21.698 24.6053 20.9032 24.9922 20.2817C25.3258 19.7482 25.1645 19.0947 24.6383 18.6977C24.3427 18.4827 23.9865 18.3668 23.6208 18.3668C23.2552 18.3668 22.899 18.4827 22.6033 18.6977L20.9469 19.949C20.3053 20.4348 19.604 20.8812 18.7689 21.0142C18.6681 21.0297 18.5627 21.0444 18.4527 21.0572M18.4527 21.0572C18.4191 21.0611 18.3855 21.0648 18.3518 21.0682M18.4527 21.0572C18.5994 21.0176 18.7339 20.942 18.8441 20.8372C18.9822 20.7178 19.095 20.5718 19.1758 20.408C19.2566 20.2442 19.3038 20.0659 19.3146 19.8835C19.3253 19.7012 19.2995 19.5186 19.2385 19.3464C19.1775 19.1742 19.0827 19.016 18.9596 18.8811C18.8401 18.7485 18.701 18.635 18.5471 18.5447C15.9832 17.0147 11.9929 18.1798 9.58301 19.8894M18.4527 21.0572C18.4195 21.0645 18.3857 21.0682 18.3518 21.0682M18.3518 21.0682C17.799 21.1238 17.2421 21.125 16.689 21.0719"
                    stroke="#0C722F"
                    stroke-linecap="round"
                  />
                  <path
                    d="M9.58301 19.2085C9.58301 18.4491 8.9674 17.8335 8.20801 17.8335C7.44862 17.8335 6.83301 18.4491 6.83301 19.2085V23.7918C6.83301 24.5512 7.44862 25.1668 8.20801 25.1668C8.9674 25.1668 9.58301 24.5512 9.58301 23.7918V19.2085Z"
                    stroke="#0C722F"
                  />
                </svg>
              </div>
              <div className="flex justify-center mt-3 gap-2">
                <p className="text-[#005C53] text-[24px] font-bold">
                  {Number(summary?.totalGuarantors || "0.00")?.toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    },
                  )}
                </p>
              </div>
              <p className="mt-3 mb-3 text-[#005C53] text-[12px]">
                Total Guarantors
              </p>
            </div>
          </div>
        </PermissionGate>

        <PermissionGate required={[PERMISSIONS?.LN?.VIEW_ALL_GUARANTORS]}>
          <div className="flex items-center justify-between w-[98%] mx-auto px-[5px] mx-auto mt-10">
            <div className="relative items-center justify-center">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                >
                  <circle
                    cx="5.35294"
                    cy="5.35294"
                    r="4.85294"
                    stroke="#999999"
                  />
                  <line
                    x1="9.52933"
                    y1="8.82294"
                    x2="13.3529"
                    y2="12.6465"
                    stroke="#999999"
                  />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={handleSearch}
                placeholder="Search by guarantor name or guarantor ID"
                className="h-[35px] pl-10 border-[1px] border-[#CCCCCC] text-[14px] rounded-[2px] w-[40vw] px-[10px] focus:outline-none"
              />
            </div>
            <div className="flex gap-4">
              <button
                className="block text-white bg-[#005C53] focus:outline-none focus:ring-[#005C53] rounded-[3px] text-center w-[10vw] h-[40px]"
                type="button"
                onClick={handleRefetch}
              >
                <div className="flex items-center justify-center gap-2">
                  {!refreshLoading && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="29"
                      height="29"
                      viewBox="0 0 29 29"
                      fill="none"
                    >
                      <path
                        d="M14.5931 22.9583C12.232 22.9583 10.2318 22.1395 8.59252 20.5018C6.95402 18.8633 6.13477 16.8639 6.13477 14.5036C6.13477 12.1433 6.95402 10.1431 8.59252 8.50303C10.2318 6.86211 12.232 6.04166 14.5931 6.04166C16.0294 6.04166 17.3739 6.38281 18.6265 7.06511C19.88 7.74742 20.8901 8.69999 21.657 9.92282V6.04166H22.8653V12.3612H16.5458V11.1529H21.0166C20.3875 9.9486 19.5022 8.99684 18.3607 8.29761C17.2208 7.5992 15.965 7.24999 14.5931 7.24999C12.5792 7.24999 10.8674 7.95485 9.45768 9.36457C8.04796 10.7743 7.3431 12.4861 7.3431 14.5C7.3431 16.5139 8.04796 18.2257 9.45768 19.6354C10.8674 21.0451 12.5792 21.75 14.5931 21.75C16.1438 21.75 17.5434 21.3069 18.7921 20.4208C20.0407 19.5347 20.9167 18.3667 21.4202 16.9167H22.7022C22.1698 18.7259 21.1576 20.1852 19.6657 21.2944C18.1738 22.4037 16.4829 22.9583 14.5931 22.9583Z"
                        fill="white"
                      />
                    </svg>
                  )}
                  {refreshLoading && (
                    <svg
                      aria-hidden="true"
                      className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  )}
                  <p className="text-[#FFF] font-normal text-[16px]">REFRESH</p>
                </div>
              </button>
              <PermissionGate required={[PERMISSIONS?.LN?.EXPORT_GUARANTORS]}>
                <ExportGuarantorExposureCSV
                  from={fromDate}
                  to={toDate}
                  search={query}
                  page={currentPage}
                  limit={limit}
                />
              </PermissionGate>
            </div>
          </div>

          <div className="bg-[#FFF] mx-[20px]">
            <div className="mt-3 h-[63px] flex px-[10px] w-full bg-[#F7FFD7]">
              <div className="flex items-center gap-[10px] w-[28vw] justify-center">
                <div className="flex items-center gap-[10px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M1.66699 4.58334C1.66699 4.25182 1.79869 3.93388 2.03311 3.69946C2.26753 3.46504 2.58547 3.33334 2.91699 3.33334H17.0837C17.4152 3.33334 17.7331 3.46504 17.9675 3.69946C18.202 3.93388 18.3337 4.25182 18.3337 4.58334C18.3337 4.91486 18.202 5.23281 17.9675 5.46723C17.7331 5.70165 17.4152 5.83334 17.0837 5.83334H2.91699C2.58547 5.83334 2.26753 5.70165 2.03311 5.46723C1.79869 5.23281 1.66699 4.91486 1.66699 4.58334ZM5.00033 9.58334C5.00033 9.25182 5.13202 8.93388 5.36644 8.69946C5.60086 8.46504 5.9188 8.33334 6.25033 8.33334H13.7503C14.0818 8.33334 14.3998 8.46504 14.6342 8.69946C14.8686 8.93388 15.0003 9.25182 15.0003 9.58334C15.0003 9.91486 14.8686 10.2328 14.6342 10.4672C14.3998 10.7016 14.0818 10.8333 13.7503 10.8333H6.25033C5.9188 10.8333 5.60086 10.7016 5.36644 10.4672C5.13202 10.2328 5.00033 9.91486 5.00033 9.58334ZM8.75033 13.3333C8.4188 13.3333 8.10086 13.465 7.86644 13.6995C7.63202 13.9339 7.50033 14.2518 7.50033 14.5833C7.50033 14.9149 7.63202 15.2328 7.86644 15.4672C8.10086 15.7016 8.4188 15.8333 8.75033 15.8333H11.2503C11.5818 15.8333 11.8998 15.7016 12.1342 15.4672C12.3686 15.2328 12.5003 14.9149 12.5003 14.5833C12.5003 14.2518 12.3686 13.9339 12.1342 13.6995C11.8998 13.465 11.5818 13.3333 11.2503 13.3333H8.75033Z"
                      fill="black"
                    />
                  </svg>
                  <p>DATE</p>
                  <Calendar
                    startDate={fromDate}
                    endDate={toDate}
                    setStartDate={setFromDate}
                    setEndDate={setToDate}
                    focusedInput={customerFocusedInput}
                    setFocusedInput={setCustomerFocusedInput}
                  />
                </div>
              </div>
            </div>

            <div className="h-[44px] bg-[#E3F8FF] w-full flex items-center mt-[2px]">
              <input
                type="checkbox"
                name="share"
                id="share"
                className="w-[15%]"
                //   checked={selectAll}
                //   onChange={handleSelectAllChange}
              />
              <p className="w-[22%] text-[14px] text-[#000000]">GUARANTOR ID</p>
              <p className="w-[38%] text-[14px] text-[#000000]">
                GUARANTOR NAME
              </p>

              <p className="w-[25%] text-[14px] text-[#000000]">
                TOTAL AMOUNT
                <br />
                GUARANTEED
              </p>
              <p className="w-[18%] text-[14px] text-[#000000]">ACTIVE LOANS</p>
              <p className="w-[25%] text-[14px] text-[#000000]">
                PENDING LOANS
              </p>
              <p className="w-[25%] text-[14px] text-[#000000]">
                RELEASED PERCENTAGE
              </p>
            </div>

            <div>
              {loadingGuarantors &&
                Array?.from({ length: 10 })?.map((_, index) => (
                  <GuarantorRowSkeleton key={index} />
                ))}
              {!loadingGuarantors &&
                guarantors?.length > 0 &&
                guarantors?.map((guarant) => (
                  <div
                    key={guarant?.id}
                    className="h-[44px] !text-[14px] bg-[#fff] w-full flex items-center"
                  >
                    <input
                      type="checkbox"
                      name="guarantor"
                      id="guarantor"
                      className="w-[15%]"
                    />
                    <PermissionGate
                      required={[PERMISSIONS?.LN?.VIEW_GUARANTORS_DETAILS]}
                    >
                      <p
                        onClick={() => {
                          setSelectedGuarantorId(guarant?.guarantorId);
                          setSelectedGuarantor(guarant?.guarantorName);
                          openRequestorsOpen();
                        }}
                        className="w-[22%] wrap text-[#06B217] cursor-pointer !text-[14px] text-[#000000]"
                      >
                        {guarant?.guarantorId}
                      </p>
                    </PermissionGate>
                    <PermissionGate
                      required={[PERMISSIONS?.TX?.VIEW_TRANSACTION_DETAILS]}
                      fallback={
                        <p className="w-[22%] wrap text-[#06B217] cursor-not-allowed  !text-[14px]">
                          {guarant?.guarantorId}
                        </p>
                      }
                    />
                    <p className="w-[38%] !text-[14px] text-[#000000]">
                      {guarant?.guarantorName
                        ?.split(" ")
                        ?.map(
                          (name) =>
                            name?.charAt(0)?.toUpperCase() +
                            name?.slice(1)?.toLowerCase(),
                        )
                        ?.join(" ")}
                    </p>

                    <p className="w-[25%] !text-[14px] text-[#000000]">
                      KES &nbsp;
                      {Number(
                        guarant?.totalGuaranteed || "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>

                    <p className="w-[18%] !text-[14px] text-[#000000]">
                      {guarant?.activeLoans}
                    </p>

                    <p className="w-[25%] !text-[14px] text-[#000000]">
                      {guarant?.pendingLoans}
                    </p>

                    <p className="w-[25%] !text-[14px] text-[#000000]">
                      {guarant?.releasedPct}
                    </p>
                  </div>
                ))}
              {!loadingGuarantors && guarantors?.length === 0 && (
                <div className="px-[20px] flex items-center mt-[20px]">
                  <p className="text-[#005C53] !text-[14px]">
                    No record of guarantors
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="h-[70px] bg-white w-[97%] px-[20px] mx-auto mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[20px]">
              <p className="text-[#005C53] !text-[14px]">Rows per page</p>
              <select
                name="page"
                id="page"
                className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="50">50</option>
                <option value="30">30</option>
                <option value="20">20</option>
                <option value="10">10</option>
              </select>
              <p className="text-[#005C53] text-[14px]">
                {displayedPage} / {totalPages} ({totalGuarantors} records)
              </p>
            </div>
            <div className="flex items-center gap-[15px]">
              <p
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                className="text-[14px] cursor-pointer text-[#005C53]"
              >
                Prev
              </p>
              {renderPages()}
              <div
                className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                  Number(currentPage) > 3
                    ? "bg-[#005C53] text-white"
                    : "bg-[#F5F5F5] text-[#005C53]"
                }`}
              >
                <p>{Number(currentPage) > 3 ? currentPage : "..."}</p>
              </div>
              <p
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages))
                }
                className="text-[14px] cursor-pointer text-[#005C53]"
              >
                Next
              </p>
            </div>
          </div>
        </PermissionGate>
        <Requestors
          isRequestorsOpen={isRequestorsOpen}
          closeRequestorsOpen={closeRequestorsOpen}
          guarantorId={selectedGuarantorId}
          selectedGuarantor={selectedGuarantor}
        />
      </div>
    </>
  );
};

export default Guarantors;
